import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { Subscription } from 'rxjs';
import { BoardConfig, resolveBoardConfig } from './board-config';
import { LiveBusDataProvider } from '../service/providers';

@Component({
  selector: 'app-depatureboard',
  templateUrl: './depatureboard.component.html',
  styleUrls: ['./depatureboard.component.css'],
  providers: [LiveBusDataProvider],
  animations: [
    trigger('fade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('500ms', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('800ms', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class DepatureboardComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private static readonly STATION_NAME_MINOR_WORDS = new Set([
    'a', 'an', 'and', 'at', 'by', 'for', 'in', 'of', 'on', 'the', 'to', 'via'
  ]);
  private static readonly NORMALIZED_FIX_STATION_NAMES = new Map<string, string[]>(
    Object.entries({
      'Caton Road': ['Park and Ride', 'Caton Road'],
      'LANCASTER, Park & Ride': ['Park and Ride'],
      'Preston Via Uni': ['Preston', 'Via Uni, Galgate & Garstang'],
      'Blackpool via Uni': ['Blackpool', 'via Uni/Galgate/Garstang'],
      'Kendal & Keswick': ['Keswick', 'via Kendal'],
      'Kendal for Keswick': ['Keswick', 'via Kendal'],
      'Kendal M6 & Keswick': ['Keswick', 'via Kendal M6'],
      'Uni Alexandra Park': ['Lancaster University'],
      'Uni, Alexandra Park': ['Lancaster University'],
      'University Underpas': ['Lancaster University'],
      "Garstang 4 B'pool": ['Blackpool', 'via Garstang'],
      'Lancaster Uni': ['Lancaster University'],
      'Morecambe Bus Stn': ['Morecambe'],
      'Preston Bus Stn': ['Preston', 'via Uni, Galgate & Garstang'],
      'Marsh Circular': ['Marsh', 'circular'],
      'Marsh': ['Marsh', 'circular'],
      'Vale': ['Vale', 'circular'],
      'Ridge Circular': ['Ridge', 'circular'],
      'Lancaster Bus Stn': ['Lancaster', 'Bus Station'],
      'Lancaster Rail Stn': ['Lancaster Rail Station'],
      'Lancaster City Centre, Lancaster': ["St. Chad's", 'via Ryelands'],
      'Lancaster Bus Stati': ['Lancaster Bus Station'],
      'KNOTT END Bourne Arms': ['Knott End'],
      'Kirkby Lonsdale, Booth': ['Kirkby Lonsdale'],
      'Kirkby Lonsdale, Booths': ['Kirkby Lonsdale'],
      'KIRKBY LONSDALE Booths': ['Kirkby Lonsdale'],
      'NEWLANDS Farmdale Road': ['Newlands', 'Farmdale Road'],
      'Morecambe via City': ['Morecambe', 'via City'],
      'Heysham via City': ['Heysham', 'via City'],
      'Carnforth Rail Stn': ['Carnforth Rail Station'],
    }).map(([key, value]) => [DepatureboardComponent.normalizeStationKey(key), value])
  );

  @Input() config: BoardConfig = resolveBoardConfig({}, '');
  @Input() allowNarrowAspect = true;
  @ViewChild('boardRoot') boardRoot?: ElementRef<HTMLElement>;

  liveBusDataProviderSubscription?: Subscription;
  clockInterval: any;
  initialMeasureFrameId?: number;
  depatures: any = [];
  liveBusData: any = [];
  liveTwitterData: any = [];

  depaturesInbound: any = [];
  depaturesOutbound: any = [];
  today: number = Date.now();
  activeMessage: boolean = false;

  busStop: string[] = this.config.busStop;
  isLightTheme: boolean = this.config.isLightTheme;
  maxDepartures: number = this.config.maxDepartures;
  showStand: boolean = this.config.showStand;
  showStopName: boolean = this.config.showStopName;
  showLastUpdated: boolean = this.config.showLastUpdated;
  directionFilter: string | boolean = this.config.directionFilter;
  dataStale: boolean = false;

  constructor(public liveBusDataProvider: LiveBusDataProvider) {
    this.clockInterval = setInterval(() => {
      this.today = Date.now();
      this.dataStale = this.liveBusDataProvider.lastFetchSuccess > 0
        && (Date.now() - this.liveBusDataProvider.lastFetchSuccess) > 100000;
    }, 1000);
  }

  ngOnInit(): void {
    this.liveBusDataProviderSubscription = this.liveBusDataProvider.data.subscribe((data) => {
      if (!data['body']) {
        return;
      }

      this.liveBusData = data['body'];
      this.depatures = [];

      this.liveBusData.sort((left, right) => left.expectedDepartureTime.localeCompare(right.expectedDepartureTime));

      for (const item of this.liveBusData) {
        let destinationName = [this.formatStationName(item.destinationName)];
        const overrideDestinationName = DepatureboardComponent.NORMALIZED_FIX_STATION_NAMES.get(
          DepatureboardComponent.normalizeStationKey(item.destinationName)
        );
        if (overrideDestinationName) {
          destinationName = overrideDestinationName;
        }

        const stand = (item.stand ?? '').toString().replace('Stand', '').trim();
        const busItem = {
          line: (item.line ?? '')
            .toString()
            .trim()
            .toUpperCase()
            .replace(/[^A-Z0-9]/g, ''),
          destination: destinationName,
          time: [],
          stand,
          direction: (item.dir ?? '').toString().toLowerCase() === 'outbound' ? 'north' : 'south'
        };

        if (this.busStop[0] === 'LANCBS001') {
          const standNum = parseInt(busItem.stand, 10);
          busItem.direction = !isNaN(standNum) && standNum < 12 ? 'south' : 'north';
        }

        const timeItem = {
          rawDelta: this.calculateDeltaRaw(item.expectedDepartureTime),
          depatureInMin: this.calculateDelta(item.expectedDepartureTime),
          isLive: item.isMonitored,
          isDelayed: item.departureStatus === 'delayed'
        };
        busItem.time.push(timeItem);

        let isDuplicate = false;
        for (const departure of this.depatures) {
          if (busItem.line === departure.line && busItem.destination[0] === departure.destination[0]) {
            if (departure.time.length < 3 && this.validTime(timeItem.rawDelta)) {
              departure.time.push(timeItem);
            }
            isDuplicate = true;
          }
        }

        if (!isDuplicate && this.validTime(busItem.time[0].rawDelta)) {
          this.depatures.push(busItem);
        }
      }

      if (this.directionFilter) {
        const direction = this.directionFilter;
        this.depatures = this.depatures.filter((departure) => departure.direction === direction);
      }

      this.depatures = this.depatures.slice(0, 6);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const configChange = changes['config'];
    if (!configChange) {
      return;
    }

    const previousLoadKey = this.getLoadKey();
    this.applyConfig(this.config);

    if (configChange.firstChange || previousLoadKey !== this.getLoadKey()) {
      this.liveBusDataProvider.load(true, this.busStop[0], this.maxDepartures);
    }
  }

  ngAfterViewInit(): void {
    window.addEventListener('resize', this.updateBoardMetrics);
    this.updateBoardMetrics();
    this.initialMeasureFrameId = requestAnimationFrame(() => {
      this.updateBoardMetrics();
      this.initialMeasureFrameId = undefined;
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.clockInterval);
    if (this.initialMeasureFrameId) {
      cancelAnimationFrame(this.initialMeasureFrameId);
    }
    window.removeEventListener('resize', this.updateBoardMetrics);
    this.liveBusDataProviderSubscription?.unsubscribe();
    this.liveBusDataProvider.stopAutoPull();
  }

  private applyConfig(config: BoardConfig): void {
    this.busStop = config.busStop;
    this.showStand = config.showStand;
    this.isLightTheme = config.isLightTheme;
    this.directionFilter = config.directionFilter;
    this.maxDepartures = config.maxDepartures;
    this.showStopName = config.showStopName;
    this.showLastUpdated = config.showLastUpdated;
  }

  private getLoadKey(): string {
    return `${this.busStop[0]}::${this.maxDepartures}`;
  }

  private updateBoardMetrics = () => {
    const componentElement = this.boardRoot?.nativeElement;
    if (!componentElement) {
      return;
    }

    const rect = componentElement.getBoundingClientRect();
    const width = rect.width || window.innerWidth;
    const height = rect.height || window.innerHeight;
    const vminUnit = Math.min(width, height) / 100;

    const isNarrow = this.allowNarrowAspect && width / height < 16 / 9;
    componentElement.classList.toggle('narrow-aspect', isNarrow);
    componentElement.style.setProperty('--board-vmin-unit', `${vminUnit}px`);
  };

  private validTime(rawDeltaMin: number) {
    const staleThresholdMin = -2;
    return rawDeltaMin < 100 && rawDeltaMin >= staleThresholdMin;
  }

  private formatStationName(raw: string): string {
    return raw.trim().split(/\s+/).map((word, index) => {
      if (/\d/.test(word)) {
        return word.toUpperCase();
      }
      const lower = word.toLowerCase();
      if (index > 0 && DepatureboardComponent.STATION_NAME_MINOR_WORDS.has(lower)) {
        return lower;
      }
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    }).join(' ');
  }

  private static normalizeStationKey(raw: string): string {
    return (raw ?? '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ');
  }

  private calculateDeltaRaw(time: string): number {
    const now = new Date();
    const arrival = new Date(time);
    return Math.round((arrival.valueOf() - now.valueOf()) / 60000);
  }

  private calculateDelta(time: string) {
    const delta = this.calculateDeltaRaw(time);
    return delta <= 0 ? 'now' : delta.toString();
  }
}
